import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Button,
  Popover,
  Skeleton,
} from "@mui/material";
import { isMobile } from "../../globalStyles/mobile";
import Pagination from "../pagination";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import FilterModal from "../filterModal/index.js";
import TableHeader from "../table-header/index.js";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import EditIcon from "../../globalStyles/icons/editIcon.js";
import DeleteIcon from "../../globalStyles/icons/deleteIcon.js";
import { historicalInventoryService } from "../../utils/services/historicalInventoryService";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/formatDate";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "1px solid var(--border-color)",
    padding: "6px",
    color: "var(--text-tertiary)",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "6px",
    color: "var(--text-secondary)",
    borderColor: "var(--border-color)",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:lastChild td, &:lastChild th": {
    border: 0,
  },
}));

const RowActionsCell = ({ item, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <Box>
      <IconButton
        aria-label="more actions"
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          e.stopPropagation();
        }}
        sx={{ color: open ? "primary.main" : "inherit" }}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(e) => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", padding: 1 }}>
          <Button
            style={{ justifyContent: "flex-start", textTransform: "none" }}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              onEdit(item);
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <EditIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                fontWeight={500}
                color={"var(--text-tertiary)"}
              >
                Edit
              </Typography>
            </Box>
          </Button>
          <Button
            style={{ justifyContent: "flex-start", textTransform: "none" }}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              onDelete(item);
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <DeleteIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                fontWeight={500}
                color={"var(--text-tertiary)"}
              >
                Delete
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

const HistoricalInventoryList = ({
  viewMode,
  data,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  page,
  dataCount,
  filterData,
  onChangeFilter,
  loading,
  fetchData,
  onEdit,
  client_id,
}) => {
  const [tableData, setTableData] = useState([]);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const startIndex = rowsPerPage * page;
  const [selectedfilterData, setSelectedFilterData] = useState({
    key: "",
    name: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "sku",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
  });

  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(filterData?.columns?.find((x) => x.key == filterKey));
  };

  const handleSaveFilter = (
    selectedOptions,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey: searchKey,
    });
    handleCloseFilter();
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setOpenConfirmation(true);
  };

  const confirmDelete = async () => {
    try {
      await historicalInventoryService.delete(selectedItem._id);
      toast.success("Historical inventory record deleted successfully!");
      fetchData && fetchData();
    } catch (error) {
      toast.error("Failed to delete historical inventory record.");
      console.error(error);
    } finally {
      setOpenConfirmation(false);
      setSelectedItem(null);
    }
  };

  useEffect(() => {
    setTableData(
      data?.map((x) => ({
        ...x,
        action: (
          <RowActionsCell
            item={x}
            onEdit={onEdit}
            onDelete={handleDeleteClick}
          />
        ),
      })),
    );
  }, [data]);

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "date",
      title: "DATE",
      handleOpenFilter,
      render: (item) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {formatDate(item?.date, "N/A")}
        </Typography>
      ),
    },

    {
      key: "product",
      title: "PRODUCT NAME",
      handleOpenFilter,
      render: (item) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "150px",
            display: "block",
          }}
        >
          {item?.product?.name || "--"}
        </Typography>
      ),
    },
    {
      key: "sku",
      title: "SKU",
      handleOpenFilter,
      displayKey: "sku",
    },
    ...(client_id
      ? []
      : [
          {
            key: "client",
            title: "CLIENT",
            handleOpenFilter,
            render: (item) => (
              <Typography
                fontSize="12px"
                color="var(--text-secondary)"
                fontWeight={400}
                fontFamily="Poppins"
              >
                {item?.client?.business_name || "--"}
              </Typography>
            ),
          },
        ]),
    {
      key: "warehouse",
      title: "WAREHOUSE",
      handleOpenFilter,
      render: (item) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {item?.warehouse?.warehouse_code || item?.warehouse?.name || "N/A"}
        </Typography>
      ),
    },
    {
      key: "quantity",
      title: "QUANTITY",
      displayKey: "quantity",
    },
    {
      key: "in_hand",
      title: "ON HAND",
      handleOpenFilter,
      displayKey: "in_hand",
    },
    {
      key: "in_transit",
      title: "COMMITTED",
      handleOpenFilter,
      displayKey: "in_transit",
    },
    { key: "action", title: "", displayKey: "action" },
  ];

  const renderGridView = () => (
    <Grid
      sx={{
        marginTop: "1px",
        maxHeight: "100%",
        paddingBottom: 5,
        overflow: "auto",
      }}
      container
      spacing={2}
    >
      {tableData?.map((x, index) => (
        <Grid
          sx={{ paddingBottom: 1 }}
          item
          xs={12}
          sm={6}
          md={4}
          key={x._id + "-historical-inventory-grid-" + index}
        >
          <Card
            sx={{
              boxShadow: 3,
              cursor: "pointer",
              width: "100%",
              height: "100%",
              position: "relative",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
            }}
          >
            <CardContent>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "start" }}
                  fontFamily="Poppins"
                  color="var(--text-tertiary)"
                >
                  {startIndex + index + 1}
                </Typography>
                <div style={{ position: "absolute", top: 10, right: 10 }}>
                  {x?.action}
                </div>
              </div>
              <Grid container spacing={1} mt={1}>
                {[
                  ["Date", formatDate(x?.date, "N/A")],
                  ["Product Name", x?.product?.name],
                  ["SKU", x?.sku],
                  ["Warehouse", x?.warehouse?.warehouse_code || "N/A"],
                  ["On Hand", x?.in_hand],
                  ["Committed", x?.in_transit ?? "--"],
                ].map(([label, value]) => (
                  <React.Fragment key={label}>
                    <Grid item xs={5} display={"flex"} alignItems={"center"}>
                      <Typography
                        fontWeight="500"
                        sx={{ textAlign: "start" }}
                        fontFamily={"poppins"}
                        color={"var(--text-tertiary)"}
                      >
                        {label}
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography
                        noWrap
                        sx={{ textAlign: "start" }}
                        fontWeight="500"
                        fontFamily={"poppins"}
                        color={"var(--text-tertiary)"}
                      >
                        : {value ?? "N/A"}
                      </Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderListView = () => (
    <div style={{ width: "100%", overflow: isMobile() ? "auto" : "hidden" }}>
      <TableContainer sx={{ overflowY: "auto", maxHeight: "100%" }}>
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{ border: "1px solid var(--border-color)" }}
        >
          <TableHead>
            <StyledTableRow>
              {TableSchema.map((column) => (
                <TableHeader
                  filterData={filterData}
                  handleOpenFilter={column.handleOpenFilter}
                  key={column.key}
                  filterKey={column.key}
                  title={column.title}
                />
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {loading
              ? [...Array(rowsPerPage || 15)].map((_, ri) => (
                  <TableRow key={ri}>
                    {TableSchema.map((col, ci) => (
                      <TableCell key={ci} sx={{ p: 1 }}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : tableData?.map((x, index) => (
                  <StyledTableRow
                    key={x._id + "-historical-inventory-" + index}
                  >
                    {TableSchema.map((column) => (
                      <StyledTableCell key={column.key}>
                        {column.displayKey ? (
                          <Typography
                            fontSize="12px"
                            color="var(--text-secondary)"
                            fontWeight={400}
                            fontFamily={"Poppins"}
                          >
                            {x[column.displayKey] ?? "--"}
                          </Typography>
                        ) : column.render ? (
                          column.render(x, index)
                        ) : null}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  return (
    <div sx={{ minWidth: "100%" }}>
      <div
        style={{
          overflowY: "scroll",
          display: "flex",
          maxHeight: isMobile() ? `calc(100vh - 200px)` : `calc(100vh - 270px)`,
          width: "100%",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {viewMode === "grid" || isMobile()
          ? renderGridView()
          : renderListView()}
      </div>
      <Pagination
        dataCount={dataCount}
        rowsPerPage={rowsPerPage}
        page={page}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        disableNext={dataCount <= rowsPerPage * (page + 1)}
      />
      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock={true}
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedfilterData?.options}
          selectedValues={selectedfilterData?.filter}
          onSave={handleSaveFilter}
          currentSortOrder={selectedfilterData?.sort}
          filterKey={selectedfilterData?.key}
          hasMore={selectedfilterData?.loadMoreButton}
          displayKey={selectedfilterData?.displayKey}
          id={selectedfilterData?.id}
          fetchApi={selectedfilterData?.fetchApi}
          searchKey={selectedfilterData?.searchKey}
          type={selectedfilterData?.type}
        />
      </Popover>
      <DeleteConfirmationModal
        title={`Are you sure you want to delete historical inventory record for SKU "${selectedItem?.sku}"?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={confirmDelete}
      />
    </div>
  );
};

export default HistoricalInventoryList;
